import React, { useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import styles from "./Sidebar.module.css";

import { useCompanyLogo } from "@hooks/useCompanyLogo";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { useFocusTrap } from "../../hooks/useFocusTrap";
import {
  HiOutlineSquares2X2,
  HiOutlineCalendar,
  HiOutlineBriefcase,
  HiOutlineInbox,
  HiOutlineCog6Tooth,
  HiOutlineClipboardDocumentList,
  HiOutlineUsers,
  HiOutlineBanknotes,
  HiChevronLeft,
  HiXMark,
} from "react-icons/hi2";
import { TbPlane } from "react-icons/tb";
import {
  SIDEBAR_NAV,
  getRolesForPath,
  getDefaultRouteForRoles,
} from "@constants/routeConfig";
import {
  WORKSPACE_SETTINGS_PATH,
  isWorkspaceSettingsPath,
} from "@constants/workspaceSettings";

const SIDEBAR_ICONS = {
  dashboard: HiOutlineSquares2X2,
  calendar: HiOutlineCalendar,
  workplace: HiOutlineInbox,
  orders: HiOutlineClipboardDocumentList,
  finance: HiOutlineBanknotes,
  customers: HiOutlineUsers,
  travelSearch: TbPlane,
  operations: HiOutlineBriefcase,
};

const SIDEBAR_LABEL_KEYS = {
  "/dashboard": "Dashboard",
  "/calendar": "nav.calendar",
  "/workplace": "Workplace",
  "/orders": "order",
  "/finance": "finance.title",
  "/customers": "customers",
  "/search": "nav.travelSearch",
  "/operations": "operations",
};

const NAV_ITEMS = SIDEBAR_NAV.map((item) => {
  const IconComponent = SIDEBAR_ICONS[item.iconKey];
  return {
    path: item.path,
    label: item.label,
    icon: IconComponent ? (
      <IconComponent aria-hidden="true" focusable="false" strokeWidth={1.5} />
    ) : null,
    roles: getRolesForPath(item.path),
  };
});

const Sidebar = ({ mobileOpen, setMobileOpen }) => {
  const { t } = useTranslation();
  const sidebarRef = useRef(null);
  const isMobile = useMediaQuery("(max-width: 768px)");
  useFocusTrap(sidebarRef, isMobile && mobileOpen);
  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem("sidebar_collapsed");
    return saved === "true";
  });

  const { pathname } = useLocation();
  const userRoles = useSelector((state) => state.user?.roles) || [];
  const company = useSelector((state) => state.user?.company);
  const canAccessWorkspaceSettings = getRolesForPath(
    WORKSPACE_SETTINGS_PATH,
  ).some((role) => userRoles.includes(role));
  const workspaceSettingsLabel = t(
    "workspaceSettings.title",
    "Workspace settings",
  );

  const { logoUrl, logoError, logoLoaded, handleLoad, handleError } =
    useCompanyLogo(company);

  const toggleCollapse = () => {
    const newState = !collapsed;
    setCollapsed(newState);
    localStorage.setItem("sidebar_collapsed", JSON.stringify(newState));
  };

  const closeMobileSidebar = () => {
    if (setMobileOpen) {
      setMobileOpen(false);
    }
  };

  return (
    <aside
      id="app-sidebar"
      ref={sidebarRef}
      tabIndex={-1}
      inert={isMobile && !mobileOpen ? "" : undefined}
      aria-hidden={isMobile && !mobileOpen ? true : undefined}
      role={isMobile && mobileOpen ? "dialog" : undefined}
      aria-modal={isMobile && mobileOpen ? true : undefined}
      aria-label={t("nav.sidebar", "Main navigation")}
      onKeyDown={(event) => {
        if (event.key === "Escape" && isMobile && mobileOpen) {
          event.preventDefault();
          closeMobileSidebar();
        }
      }}
      className={`${styles.sidebar} ${collapsed ? styles.collapsed : ""} ${mobileOpen ? styles.mobileOpen : ""}`}
    >
      {/* Header */}
      <div className={styles.header}>
        <Link
          to={getDefaultRouteForRoles(userRoles)}
          className={styles.headerContent}
          onClick={closeMobileSidebar}
          aria-label={t("nav.home", "Go to home")}
          title={t("nav.home", "Go to home")}
        >
          <div className={styles.companyLogoWrapper}>
            {!logoUrl || logoError ? (
              <div className={styles.companyLogoPlaceholder}>
                {(company?.name || "A").charAt(0).toUpperCase()}
              </div>
            ) : (
              <>
                <div
                  className={`${styles.companyLogoPlaceholder} ${styles.companyLogoPlaceholderOverlay} ${logoLoaded ? styles.companyLogoPlaceholderHidden : ""}`}
                  aria-hidden="true"
                >
                  {(company?.name || "A").charAt(0).toUpperCase()}
                </div>
                <img
                  src={logoUrl}
                  alt={company?.name || t("companyProfile.company", "Company")}
                  className={`${styles.companyLogo} ${logoLoaded ? styles.companyLogoLoaded : ""}`}
                  decoding="async"
                  crossOrigin="anonymous"
                  onLoad={handleLoad}
                  onError={handleError}
                />
              </>
            )}
          </div>
          <div className={styles.companyNameWrapper}>
            <span className={styles.companyName}>
              {company?.name || "AVIAFY"}
            </span>
          </div>
        </Link>

        {/* Desktop Collapse Button */}
        <button
          type="button"
          className={styles.collapseBtn}
          onClick={toggleCollapse}
          aria-label={
            collapsed
              ? t("common.expandSidebar", "Expand sidebar")
              : t("common.collapseSidebar", "Collapse sidebar")
          }
        >
          {collapsed ? (
            <HiChevronLeft
              size={20}
              color="#6b7280"
              style={{ transform: "rotate(180deg)" }}
            />
          ) : (
            <HiChevronLeft size={20} color="#6b7280" />
          )}
        </button>

        {/* Mobile Close Button */}
        <button
          type="button"
          className={styles.mobileCloseBtn}
          onClick={closeMobileSidebar}
          aria-label={t("common.closeSidebar", "Close sidebar")}
        >
          <HiXMark size={24} color="#6b7280" />
        </button>
      </div>

      {/* Body */}
      <nav className={styles.body}>
        {NAV_ITEMS.map((item) => {
          const label = t(SIDEBAR_LABEL_KEYS[item.path], item.label);
          const hasPermission = item.roles.some((role) =>
            userRoles.includes(role),
          );
          if (!hasPermission) return null;

          return (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={closeMobileSidebar}
              className={({ isActive }) =>
                `${styles.navItem} ${isActive ? styles.active : ""}`
              }
              title={label}
              aria-label={label}
              data-tooltip={label}
            >
              <div className={styles.iconWrapper}>{item.icon}</div>
              <div className={styles.labelWrapper}>
                <span className={styles.label}>{label}</span>
              </div>
            </NavLink>
          );
        })}
      </nav>

      {/* Footer */}
      {canAccessWorkspaceSettings && (
        <div className={styles.footer}>
          <nav className={styles.footerNav} aria-label={workspaceSettingsLabel}>
            <NavLink
              to={WORKSPACE_SETTINGS_PATH}
              onClick={closeMobileSidebar}
              className={`${styles.navItem} ${isWorkspaceSettingsPath(pathname) ? styles.active : ""}`}
              title={workspaceSettingsLabel}
              aria-label={workspaceSettingsLabel}
              data-tooltip={workspaceSettingsLabel}
            >
              <div className={styles.iconWrapper}>
                <HiOutlineCog6Tooth aria-hidden="true" focusable="false" />
              </div>
              <div className={styles.labelWrapper}>
                <span className={styles.label}>{workspaceSettingsLabel}</span>
              </div>
            </NavLink>
          </nav>
        </div>
      )}
    </aside>
  );
};

export default Sidebar;
